import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";
const MIN_LENGTH = 8;

// The server's answer can be {detail: "text"}, {detail: {code, message}} or a list of field errors.
const messageFrom = (body, fallback) => {
  const d = body && body.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  if (Array.isArray(d) && d[0] && d[0].msg) return d[0].msg;
  return fallback;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(true);
      } else if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError(messageFrom(body, 'Something went wrong. Please request a new link.'));
      }
    } catch (err) {
      setError('We could not reach RideBuraq. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="RideBuraq" className="w-14 h-14 object-contain mb-5" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Account help</p>
          <h1 className="text-4xl font-semibold tracking-tight">Choose a new password</h1>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">{children}</div>
      </div>
    </div>
  );

  if (!token) {
    return shell(
      <div data-testid="reset-no-token" className="text-center space-y-3">
        <p className="text-lg font-medium">This reset link is not complete.</p>
        <p className="text-sm text-gray-400">Open the link from your email again, or request a new one.</p>
        <Link to="/forgot-password" className="inline-block text-orange-400 hover:text-orange-300 font-medium text-sm">Request a new link</Link>
      </div>
    );
  }

  if (done) {
    return shell(
      <div data-testid="reset-done" className="text-center space-y-3">
        <p className="text-lg font-medium">Your password has been changed.</p>
        <p className="text-sm text-gray-400">You were signed out everywhere. Sign in with your new password.</p>
        <button
          onClick={() => navigate('/login')}
          className="w-full rounded-full py-3.5 mt-2 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600"
        >
          Go to sign in
        </button>
      </div>
    );
  }

  return shell(
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="password" className="text-gray-400 text-xs tracking-wide uppercase">New password</Label>
        <Input
          id="password" name="password" type="password" required autoComplete="new-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl"
          placeholder="At least 8 characters"
        />
      </div>
      <div>
        <Label htmlFor="confirm" className="text-gray-400 text-xs tracking-wide uppercase">Confirm new password</Label>
        <Input
          id="confirm" name="confirm" type="password" required autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl"
          placeholder="Type it again"
        />
      </div>
      {error && (
        <p data-testid="reset-error" className="text-sm text-red-400">
          {error}{' '}
          {/(invalid|expired)/i.test(error) && <Link to="/forgot-password" className="underline text-orange-400">Request a new link</Link>}
        </p>
      )}
      <button
        type="submit"
        data-testid="reset-submit-btn"
        disabled={loading}
        className="w-full rounded-full py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-60 flex items-center justify-center"
      >
        {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Change password'}
      </button>
    </form>
  );
};

export default ResetPassword;
